import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { api, formatTarih, MUHASEBE_AD } from '../lib/api';
import { useAuth } from '../lib/auth';
import { VadeAyar } from '../ui/VadeTanim';
import FirmaAyar from '../ui/FirmaAyar';
import { ResmiBelgeAyar } from '../ui/ResmiBelge';
import AyarPaneli, { ayarPaneliVar, gorunenAyarBolumleri, useGenisEkran } from '../ui/AyarPaneli';
import Ikon from '../ui/Ikon';
import { ESKI_CAPA_BOLUMU } from '../kilavuz/ayarDizini';
import { HatirlatmaAyarlar, type AyarVeri as HatirlatmaAyarVeri } from './BorcHatirlatma';
import Kasa from './Kasa';
import Parola from './Parola';
import SiteAyarlar from './site/SiteAyarlar';
import WhatsappAyar from './ayar/WhatsappAyar';
import { TelegramBenim, TelegramYonetim } from '../ui/TelegramAyar';

type AyarSatiri = { key: string; ad: string; aciklama: string; tip: string; deger: unknown };
type Rol = { id: string; ad: string; yetkiler: string[] };
type Veri = {
  satirlar: AyarSatiri[];
  iskonto: { markalar: Record<string, number>; dogrulandi: boolean; not: string };
  roller: Rol[];
};
type Kullanici = {
  id: number; name: string; username: string; role: string; roleLabel: string;
  phone: string | null; email: string | null; active: boolean;
  telegramBagli: boolean; acikOturum: number; createdAt: string;
};

/**
 * AYARLAR (şartname 4.7) — ikinci düzey panel düzeni: `/ayarlar/:bolum`.
 *
 * Her bölüm ayrı sayfadır; sol yandaki liste `ui/AyarPaneli.tsx`'ten gelir (kabuk çizer).
 * Başka ekranın sekmesinde duran ayarlar (Borç Hatırlatma › Ayarlar, Kasa › Tanımlar)
 * orada da durur; burada AYNI bileşen gösterilir, ikinci kopya yoktur.
 * Eski tek sayfalık adresler (`/ayarlar#vade`) yeni karşılığına yönlenir.
 */
export default function Ayarlar() {
  const { bolum } = useParams();
  const { hash } = useLocation();
  const { can } = useAuth();
  const genis = useGenisEkran();
  const gorunen = gorunenAyarBolumleri(can);

  // Menüdeki kuralın aynısı: paneli göremeyen rol adresi elle yazsa da açılmaz (ayarların kendi ekranları ayrıca korunur).
  if (!ayarPaneliVar(can)) return <Navigate to="/" replace />;
  if (!bolum) {
    const eski = ESKI_CAPA_BOLUMU[decodeURIComponent(hash.replace('#', ''))];
    if (eski && gorunen.some((b) => b.id === eski)) return <Navigate to={`/ayarlar/${eski}${hash}`} replace />;
    // Geniş ekranda liste yan panelde durur: ilk bölüm açılır. Dar ekranda liste içeriğin kendisidir.
    if (genis) return <Navigate to={`/ayarlar/${gorunen[0].id}`} replace />;
    return <AyarPaneli gomulu />;
  }
  const b = gorunen.find((x) => x.id === bolum);
  if (!b) return <Navigate to="/ayarlar" replace />;
  // Kendi sayfa başlığını taşıyan gömülü ekranlarda ikinci başlık basılmaz.
  const kendiBasligi = b.id === 'site' || b.id === 'parola';

  return (
    <>
      <Link to="/ayarlar" className="ccd-ayar-geri"><Ikon ad="geri" boy={16} /> Ayarlar</Link>
      {!kendiBasligi && (
        <div>
          <h1 className="ccd-title">{b.ad}</h1>
          <div className="ccd-meta">{b.aciklama}</div>
        </div>
      )}
      {b.id === 'genel' && <FirmaAyar />}
      {(b.id === 'esikler' || b.id === 'iskonto' || b.id === 'kullanicilar') && <TemelBolum goster={b.id} />}
      {b.id === 'telegram' && <><TelegramBenim /><TelegramYonetim /></>}
      {b.id === 'vade' && <VadeAyar />}
      {b.id === 'resmi-belge' && <div id="resmi-belge"><ResmiBelgeAyar /></div>}
      {b.id === 'borc-hatirlatma' && <HatirlatmaBolumu />}
      {b.id === 'whatsapp' && <WhatsappAyar />}
      {b.id === 'kasa' && <Kasa yalnizTanim />}
      {b.id === 'stok-disi' && <StokDisiBolumu />}
      {b.id === 'katalog' && <KatalogBolumu />}
      {b.id === 'muhasebe' && <MuhasebeBolumu />}
      {b.id === 'site' && <SiteAyarlar />}
      {b.id === 'parola' && <Parola />}
    </>
  );
}

const Hata = ({ m }: { m: string }) => <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{m}</span></div>;

/** Borç hatırlatma ayarları: Borç Hatırlatma › Ayarlar sekmesindeki bileşenin aynısı. */
function HatirlatmaBolumu() {
  const [veri, setVeri] = useState<HatirlatmaAyarVeri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [surum, setSurum] = useState(0);
  useEffect(() => {
    api<HatirlatmaAyarVeri>('/api/hatirlatma/ayar').then(setVeri).catch((e) => setHata(e instanceof Error ? e.message : 'Ayar okunamadı'));
  }, [surum]);
  if (hata) return <Hata m={hata} />;
  if (!veri) return <div className="ccd-inline-note">Yükleniyor…</div>;
  return (
    <>
      <HatirlatmaAyarlar veri={veri} kaydedildi={() => setSurum((n) => n + 1)} />
      <div className="ccd-meta">Borçlular, onay bekleyen iletiler ve geçmiş: <Link className="ccd-link" to="/tahsilat/hatirlatma">Borç Hatırlatma ekranı</Link>.</div>
    </>
  );
}

/** Stok tutmayan kategoriler tutarlarıyla birlikte Tanımlar ve Bakiyeler'de işaretlenir; burası oraya götürür. */
function StokDisiBolumu() {
  return (
    <div className="ccd-card ccd-stack-sm" id="stok-disi">
      <div className="ccd-fieldset-title">Stok tutmayan kategoriler</div>
      <div className="ccd-meta">
        Hizmet, nakliye, montaj gibi kategorilerde stok tutulmaz. Bir kategoriyi "stok tutmaz" işaretlerseniz o kategorideki
        faturalar stok hareketi üretmez ve eşleme kuyruğuna düşmez. İşaret, kategorilerin tutarlarıyla birlikte listelendiği
        ekranda verilir.
      </div>
      <div><Link className="ccd-btn ccd-btn-primary ccd-btn-sm" to="/finans?sekme=kategori">Kategorileri aç</Link></div>
    </div>
  );
}

type KatalogDurum = { son: { at?: string; adet?: number; kaynak?: Record<string, number>; hata?: string; denendi?: string } | null };

/** Tedarikçi kataloğu: son güncelleme durumu ve elle yenileme (uçlar: `/api/katalog/durum`, `/api/katalog/yenile`). */
function KatalogBolumu() {
  const { can } = useAuth();
  const [d, setD] = useState<KatalogDurum | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const yukle = useCallback(() => {
    api<KatalogDurum>('/api/katalog/durum').then(setD).catch((e) => setHata(e instanceof Error ? e.message : 'Durum okunamadı'));
  }, []);
  useEffect(yukle, [yukle]);

  async function yenile() {
    if (!window.confirm('Tedarikçi kataloğu şimdi yenilensin mi?\n\nKaynaklardan güncel ürün ve liste fiyatları okunur; birkaç dakika sürebilir.')) return;
    setMesgul(true); setHata(null); setBilgi(null);
    try {
      const r = await api<{ adet?: number }>('/api/katalog/yenile', { method: 'POST' });
      setBilgi(`Katalog yenilendi${r.adet != null ? `: ${r.adet.toLocaleString('tr-TR')} ürün` : ''}.`);
      yukle();
    } catch (e) { setHata(e instanceof Error ? e.message : 'Yenilenemedi'); }
    finally { setMesgul(false); }
  }

  return (
    <div className="ccd-card ccd-stack-sm" id="katalog">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div className="ccd-fieldset-title">Tedarikçi kataloğu</div>
          <div className="ccd-meta">Teklifte stokta olmayan ürünleri bulmak için kullanılan dış katalog. Kendiliğinden 6 saatte bir güncellenir.</div>
        </div>
        {can('settings.write') && <button className="ccd-btn ccd-btn-sm" onClick={() => void yenile()} disabled={mesgul}>{mesgul ? 'Yenileniyor…' : 'Şimdi yenile'}</button>}
      </div>
      {bilgi && <div className="ccd-banner" role="status"><span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span></div>}
      {hata && <Hata m={hata} />}
      {!d ? (!hata && <div className="ccd-inline-note">Yükleniyor…</div>) : !d.son ? (
        <div className="ccd-meta">Katalog henüz hiç indirilmedi.</div>
      ) : (
        <div className="ccd-ayar-ozet">
          <div><span className="ccd-meta">Son güncelleme</span><b>{d.son.at ? formatTarih(d.son.at) : '—'}</b></div>
          <div><span className="ccd-meta">Ürün sayısı</span><b>{d.son.adet != null ? d.son.adet.toLocaleString('tr-TR') : '—'}</b></div>
          {Object.entries(d.son.kaynak ?? {}).map(([ad, n]) => (
            <div key={ad}><span className="ccd-meta">{ad}</span><b>{Number(n).toLocaleString('tr-TR')}</b></div>
          ))}
        </div>
      )}
      {d?.son?.hata && <div className="ccd-meta ccd-kpi-down">Son denemede hata: {d.son.hata}</div>}
    </div>
  );
}

type MuhasebeDurum = {
  kip: string; uyari: string | null; sonEsitleme: string | null; esitlemeAraligiMs: number;
  sonSatisFaturasi: { no: string | null; tarih: string } | null; bekleyen: { satisFaturasi: number; alisFaturasi: number };
};

/** Muhasebe programı: bağlantı durumu (uç: `/api/parasut/durum`). Eşitleme düğmesi Alış Faturaları ekranındadır. */
function MuhasebeBolumu() {
  const [d, setD] = useState<MuhasebeDurum | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  useEffect(() => {
    api<MuhasebeDurum>('/api/parasut/durum').then(setD).catch((e) => setHata(e instanceof Error ? e.message : 'Durum okunamadı'));
  }, []);
  return (
    <div className="ccd-card ccd-stack-sm" id="muhasebe">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div className="ccd-fieldset-title">{MUHASEBE_AD} bağlantısı</div>
          <div className="ccd-meta">Satış ve alış faturaları {MUHASEBE_AD}'ten okunur. Bağlantı bilgileri sunucudaki ayar dosyasındadır; panelden değiştirilmez.</div>
        </div>
        {d && <span className={`ccd-badge${d.kip === 'real' ? ' ccd-badge-success' : d.kip === 'mock' ? ' ccd-badge-warning' : ''}`}><span className="ccd-badge-dot" />
          {d.kip === 'real' ? 'Bağlı' : d.kip === 'mock' ? 'MOCK — örnek veri' : 'Bağlanmadı'}</span>}
      </div>
      {hata && <Hata m={hata} />}
      {!d ? (!hata && <div className="ccd-inline-note">Yükleniyor…</div>) : (
        <>
          {d.uyari && <div className="ccd-meta ccd-kpi-down">{d.uyari}</div>}
          <div className="ccd-ayar-ozet">
            <div><span className="ccd-meta">Son eşitleme</span><b>{d.sonEsitleme ? formatTarih(d.sonEsitleme) : '—'}</b></div>
            <div><span className="ccd-meta">Kendiliğinden eşitleme</span><b>{Math.max(1, Math.round(d.esitlemeAraligiMs / 60000))} dakikada bir</b></div>
            <div><span className="ccd-meta">Son satış faturası</span><b>{d.sonSatisFaturasi ? `${d.sonSatisFaturasi.no ?? 'numarasız'} · ${formatTarih(d.sonSatisFaturasi.tarih)}` : '—'}</b></div>
            <div><span className="ccd-meta">İşlenmeyi bekleyen satış faturası</span><b>{d.bekleyen.satisFaturasi}</b></div>
            <div><span className="ccd-meta">Eşleme bekleyen alış faturası</span><b>{d.bekleyen.alisFaturasi}</b></div>
          </div>
          <div className="ccd-meta">Elle eşitleme ve ürün eşleme kuyruğu: <Link className="ccd-link" to="/alis">Alış Faturaları</Link>.</div>
        </>
      )}
    </div>
  );
}

/**
 * Ayar tablosundan (`/api/ayarlar`) beslenen bölümler: iskonto, eşikler, Telegram, kullanıcılar ve roller.
 *
 * İskonto tablosu burada düzenleniyor çünkü teklifteki TAVAN bundan türüyor;
 * "DOĞRULANACAK" rozeti Mehmetcan gerçek oranları verene kadar duruyor.
 */
function TemelBolum({ goster }: { goster: 'esikler' | 'iskonto' | 'kullanicilar' }) {
  const { can } = useAuth();
  const [veri, setVeri] = useState<Veri | null>(null);
  const [kullanicilar, setKullanicilar] = useState<Kullanici[]>([]);
  const [taslak, setTaslak] = useState<Record<string, string>>({});
  const [iskontoTaslak, setIskontoTaslak] = useState<Record<string, string>>({});
  const [yeniMarka, setYeniMarka] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [yeniKullaniciAcik, setYeniKullaniciAcik] = useState(false);

  const yukle = useCallback(() => {
    api<Veri>('/api/ayarlar').then((r) => {
      setVeri(r);
      setTaslak(Object.fromEntries(r.satirlar
        .filter((s) => s.tip !== 'nesne')
        .map((s) => [s.key, s.tip === 'liste' ? (s.deger as number[] ?? []).join(', ') : String(s.deger ?? '')])));
      setIskontoTaslak(Object.fromEntries(
        Object.entries(r.iskonto.markalar).map(([m, o]) => [m, String(o)])
      ));
    }).catch((e) => setHata((e as Error).message));
    if (can('user.manage')) {
      api<{ kullanicilar: Kullanici[] }>('/api/kullanicilar')
        .then((r) => setKullanicilar(r.kullanicilar)).catch(() => undefined);
    }
  }, [can]);
  useEffect(yukle, [yukle]);

  async function ayarKaydet(s: AyarSatiri) {
    setHata(null);
    const ham = taslak[s.key] ?? '';
    if (ham.trim() === '') { setHata(`${s.ad} boş bırakılamaz`); return; }
    const deger = s.tip === 'liste'
      ? ham.split(',').map((x) => Number(x.trim())).filter((x) => Number.isFinite(x))
      : s.tip === 'sayi' ? Number(ham) : ham;
    try {
      await api(`/api/ayarlar/${s.key}`, { method: 'PUT', body: JSON.stringify({ deger }) });
      setBilgi(`${s.ad} kaydedildi`);
      yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Kaydedilemedi');
    }
  }

  async function iskontoKaydet() {
    setHata(null);
    const bozuk = Object.entries(iskontoTaslak).find(([, v]) => v.trim() === '' || !Number.isFinite(Number(v)) || Number(v) < 0 || Number(v) > 100);
    if (bozuk) { setHata(`${bozuk[0]}: iskonto oranı 0 ile 100 arasında bir sayı olmalı`); return; }
    const markalar = Object.fromEntries(
      Object.entries(iskontoTaslak)
        .filter(([, v]) => v !== '' && Number.isFinite(Number(v)))
        .map(([m, v]) => [m, Number(v)])
    );
    try {
      await api(`/api/ayarlar/alis_iskontosu`, {
        method: 'PUT',
        body: JSON.stringify({
          deger: {
            markalar, dogrulandi: true,
            not: `Panelden güncellendi · ${new Date().toLocaleDateString('tr-TR')}`,
          },
        }),
      });
      setBilgi('İskonto tablosu kaydedildi — teklifteki tavanlar bundan türüyor');
      yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Kaydedilemedi');
    }
  }

  async function kullaniciDegistir(id: number, alan: Record<string, unknown>) {
    setHata(null);
    try {
      await api(`/api/kullanicilar/${id}`, { method: 'PATCH', body: JSON.stringify(alan) });
      yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Değiştirilemedi');
    }
  }

  if (hata && !veri) {
    return <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>;
  }
  if (!veri) return <div className="ccd-inline-note">Yükleniyor…</div>;

  return (
    <>
      {bilgi && <div className="ccd-banner"><span className="ccd-banner-icon">✓</span><span>{bilgi}</span></div>}
      {hata && (
        <div className="ccd-banner ccd-banner-danger">
          <span className="ccd-banner-icon">!</span><span>{hata}</span>
        </div>
      )}

      {/* İskonto tablosu */}
      {goster === 'iskonto' && <div className="ccd-card ccd-stack-sm" id="iskonto">
        <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="ccd-fieldset-title" data-tur="iskonto">Marka alış iskontoları (%)</div>
            <div className="ccd-meta">
              Teklifteki <b>iskonto tavanı</b> buradan türüyor. Tavanı aşan satır kırmızı
              uyarı alır ama <b>engellenmez</b>.
            </div>
          </div>
          {!veri.iskonto.dogrulandi && (
            <span className="ccd-badge ccd-badge-warning">DOĞRULANACAK</span>
          )}
        </div>

        {!veri.iskonto.dogrulandi && (
          <div className="ccd-meta ccd-kpi-down">{veri.iskonto.not}</div>
        )}

        {Object.keys(iskontoTaslak).map((marka) => (
          <div key={marka} className="ccd-row" style={{ alignItems: 'flex-end' }}>
            <div className="ccd-field" style={{ flex: 1 }}>
              <label className="ccd-field-label">{marka}</label>
              <input className="ccd-input" type="number" inputMode="decimal" step="any" min={0} max={100}
                     style={{ maxWidth: 120 }}
                     value={iskontoTaslak[marka]}
                     onChange={(e) => setIskontoTaslak((x) => ({ ...x, [marka]: e.target.value }))} />
            </div>
            {can('settings.write') && (
              <button className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                      onClick={() => setIskontoTaslak((x) => {
                        const y = { ...x }; delete y[marka]; return y;
                      })}>
                Kaldır
              </button>
            )}
          </div>
        ))}

        {can('settings.write') && (
          <>
            <div className="ccd-row" style={{ alignItems: 'flex-end' }} id="iskonto-ekle">
              <div className="ccd-field" style={{ flex: 1 }}>
                <label className="ccd-field-label">Marka ekle</label>
                <input className="ccd-input" placeholder="Kale" value={yeniMarka}
                       onChange={(e) => setYeniMarka(e.target.value)} />
              </div>
              <button className="ccd-btn ccd-btn-sm" disabled={!yeniMarka.trim()}
                      onClick={() => {
                        setIskontoTaslak((x) => ({ ...x, [yeniMarka.trim()]: '' }));
                        setYeniMarka('');
                      }}>
                Ekle
              </button>
            </div>
            <button className="ccd-btn ccd-btn-primary" onClick={iskontoKaydet}>
              İskonto tablosunu kaydet
            </button>
          </>
        )}
      </div>}

      {/* Sayısal ayarlar */}
      {goster === 'esikler' && <div className="ccd-card ccd-stack-sm" id="esikler">
        <div className="ccd-fieldset-title">Eşikler ve süreler</div>
        {veri.satirlar.filter((s) => s.tip !== 'nesne').map((s) => (
          <div key={s.key} className="ccd-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="ccd-field" style={{ flex: '1 1 260px' }}>
              <label className="ccd-field-label" htmlFor={s.key}>{s.ad}</label>
              <input id={s.key} className="ccd-input"
                     type={s.tip === 'sayi' ? 'number' : 'text'}
                     value={taslak[s.key] ?? ''}
                     disabled={!can('settings.write')}
                     onChange={(e) => setTaslak((x) => ({ ...x, [s.key]: e.target.value }))} />
              <div className="ccd-field-hint">{s.aciklama}</div>
            </div>
            {can('settings.write') && (
              <button className="ccd-btn ccd-btn-sm" onClick={() => ayarKaydet(s)}>Kaydet</button>
            )}
          </div>
        ))}
      </div>}

      {/* Kullanıcılar */}
      {goster === 'kullanicilar' && can('user.manage') && (
        <div className="ccd-card ccd-stack-sm" id="kullanicilar">
          <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div className="ccd-fieldset-title">Kullanıcılar ({kullanicilar.length})</div>
            <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => setYeniKullaniciAcik(true)} data-tur="kullanici">
              + Kullanıcı
            </button>
          </div>
          <div className="ccd-table-wrap">
            <table className="ccd-table">
              <thead>
                <tr>
                  <th className="ccd-th">Kişi</th>
                  <th className="ccd-th">Rol</th>
                  <th className="ccd-th">Telegram</th>
                  <th className="ccd-th ccd-td-num">Oturum</th>
                  <th className="ccd-th">Durum</th>
                </tr>
              </thead>
              <tbody>
                {kullanicilar.map((k) => (
                  <tr key={k.id} className="ccd-tr">
                    <td className="ccd-td">
                      <b>{k.name}</b>
                      <div className="ccd-meta ccd-mono">{k.username}</div>
                    </td>
                    <td className="ccd-td">
                      <select className="ccd-select" value={k.role}
                              onChange={(e) => { if (window.confirm(`${k.name} için rol değişsin mi?`)) kullaniciDegistir(k.id, { role: e.target.value }); }}>
                        {veri.roller.map((r) => (
                          <option key={r.id} value={r.id}>{r.ad}</option>
                        ))}
                      </select>
                    </td>
                    <td className="ccd-td">
                      {k.telegramBagli
                        ? <span className="ccd-badge ccd-badge-success">bağlı</span>
                        : <span className="ccd-muted">—</span>}
                    </td>
                    <td className="ccd-td ccd-td-num">{k.acikOturum}</td>
                    <td className="ccd-td">
                      <button className={`ccd-btn ccd-btn-sm${k.active ? '' : ' ccd-btn-primary'}`}
                              onClick={() => { if (!k.active || window.confirm(`${k.name} pasife alınsın mı? Açık oturumları kapanır.`)) kullaniciDegistir(k.id, { active: !k.active }); }}>
                        {k.active ? 'Pasife al' : 'Aktifleştir'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rol matrisi */}
      {goster === 'kullanicilar' && <div className="ccd-card" id="roller">
        <div className="ccd-fieldset-title">Rol → yetki matrisi</div>
        <div className="ccd-meta" style={{ marginBottom: 8 }}>
          Bu matris hem arayüzü hem API'yi besler — arayüzde gizlenen bir şey API'de de kapalıdır.
        </div>
        <div className="ccd-table-wrap">
          <table className="ccd-table">
            <thead>
              <tr>
                <th className="ccd-th">Rol</th>
                <th className="ccd-th ccd-td-num">Yetki</th>
                <th className="ccd-th">Öne çıkanlar</th>
              </tr>
            </thead>
            <tbody>
              {veri.roller.map((r) => (
                <tr key={r.id} className="ccd-tr">
                  <td className="ccd-td"><b>{r.ad}</b><div className="ccd-meta ccd-mono">{r.id}</div></td>
                  <td className="ccd-td ccd-td-num">{r.yetkiler.length}</td>
                  <td className="ccd-td ccd-meta">
                    {['quote.cost', 'informal.view', 'receivable.view', 'settings.write']
                      .map((y) => `${r.yetkiler.includes(y) ? '✓' : '✗'} ${y}`)
                      .join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>}

      {yeniKullaniciAcik && (
        <YeniKullanici roller={veri.roller}
                       kapat={() => setYeniKullaniciAcik(false)}
                       eklendi={() => { setYeniKullaniciAcik(false); yukle(); }} />
      )}
    </>
  );
}

/* ─── Yeni kullanıcı ───────────────────────────────────────────────────── */
function YeniKullanici({
  roller, kapat, eklendi,
}: { roller: Rol[]; kapat: () => void; eklendi: () => void }) {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('SATIS');
  const [phone, setPhone] = useState('');
  const [parola, setParola] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);

  /** Parolayı kullanıcı uydurmasın: güçlü ve okunur bir tane üretiliyor. */
  function parolaUret() {
    const harfler = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const d = new Uint32Array(14);
    crypto.getRandomValues(d);
    setParola(Array.from(d, (x) => harfler[x % harfler.length]).join(''));
  }

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    setHata(null); setBekliyor(true);
    try {
      await api('/api/kullanicilar', {
        method: 'POST',
        body: JSON.stringify({ name, username, role, phone: phone || undefined, parola }),
      });
      eklendi();
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Eklenemedi');
    } finally {
      setBekliyor(false);
    }
  }

  return (
    <div className="ccd-drawer-arka" onClick={kapat} role="presentation">
      <aside className="ccd-drawer" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-modal="true" aria-label="Yeni kullanıcı">
        <form className="ccd-stack" onSubmit={gonder}>
          <div className="ccd-row-between">
            <div>
              <div className="ccd-eyebrow">Sistem</div>
              <h2 className="ccd-heading">Yeni kullanıcı</h2>
            </div>
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Kapat</button>
          </div>

          <div className="ccd-form-grid">
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="kad">
                Ad soyad<span className="ccd-required"> *</span>
              </label>
              <input id="kad" className="ccd-input" required value={name}
                     onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="kkul">
                Kullanıcı adı<span className="ccd-required"> *</span>
              </label>
              <input id="kkul" className="ccd-input ccd-mono" required value={username}
                     onChange={(e) => setUsername(e.target.value)} />
            </div>
          </div>

          <div className="ccd-form-grid">
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="krol">Rol</label>
              <select id="krol" className="ccd-select" value={role} onChange={(e) => setRole(e.target.value)}>
                {roller.map((r) => <option key={r.id} value={r.id}>{r.ad}</option>)}
              </select>
            </div>
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="ktel">Telefon</label>
              <input id="ktel" className="ccd-input" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>

          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="kpar">
              Parola<span className="ccd-required"> *</span>
            </label>
            <div className="ccd-row" style={{ alignItems: 'center' }}>
              <input id="kpar" className="ccd-input ccd-mono" required minLength={8}
                     value={parola} onChange={(e) => setParola(e.target.value)} />
              <button type="button" className="ccd-btn ccd-btn-sm" onClick={parolaUret}>Üret</button>
            </div>
            <div className="ccd-field-hint">
              Kullanıcıya iletin; ilk girişte kendisi değiştirebilir (Parola ekranı).
            </div>
          </div>

          {hata && (
            <div className="ccd-banner ccd-banner-danger">
              <span className="ccd-banner-icon">!</span><span>{hata}</span>
            </div>
          )}

          <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>
            {bekliyor ? 'Ekleniyor…' : 'Kullanıcıyı ekle'}
          </button>
        </form>
      </aside>
    </div>
  );
}
